import { createCustomAttributesSchema } from "@aurem/components/src/controls";
import { FlattenedTranslations } from "providers/I18nProvider";
import { CustomAttribute } from "types";
import { z } from "zod";

export enum CreateCompanySourceType {
  Mohre = "Mohre",
  Manual = "Manual",
}

export const createCompanyMohreInfoSchema = (
  t: (key: keyof FlattenedTranslations) => string,
) => {
  return z
    .object({
      uid: z
        .string()
        .refine(
          (value) => valueIsBlankOrLengthInRange(value, 6, 15),
          t(
            "apps.admin.pages.companies.new.page.company.mohreForm.info.uid.required",
          ),
        )
        .optional(),
      eida: z
        .string()
        .refine(
          (value) => valueIsBlankOrCorrectLength(value, 15),
          t(
            "apps.admin.pages.companies.new.page.company.mohreForm.info.eida.required",
          ),
        )
        .optional(),
      compCode: z.string().optional(),
      ownerCode: z.string().optional(),
      icpCompanyCode: z.string().optional(),
      employerId: z.string().optional(),
      registeredNumber: z.string().optional(),
      fullDetails: z.string().optional(),
      name: z.string().optional(),
      address: z.string().optional(),
      industry: z.string().optional(),
      companyId: z.string().optional(),
      emirate: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (!data.eida?.trim() && !data.uid?.trim()) {
        const message = t(
          "apps.admin.pages.companies.new.page.company.mohreForm.info.eidaOrUid.required",
        );
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message,
          path: ["eida"],
        });
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message,
          path: ["uid"],
        });
      }
    });
};

export type CompanyMohreInfoFormInput = z.infer<
  ReturnType<typeof createCompanyMohreInfoSchema>
>;

export enum MohreStep {
  Start,
  Otp,
  SelectCompany,
  CompanyRetrieved,
  CompanyError,
  Preview,
  Edit,
}

export const valueIsBlankOrCorrectLength = (
  value: string | undefined,
  length: number,
): boolean =>
  !value || (value?.trim().replace(/-/g, "") || "").length === length;

export const valueIsBlankOrLengthInRange = (
  value: string | undefined,
  min: number,
  max: number,
): boolean => {
  if (!value) return true;
  const length = (value.trim().replace(/-/g, "") || "").length;
  return length >= min && length <= max;
};

export const manualCompanyFormSchema = (
  t: (s: string, ...args: unknown[]) => string,
  customAttributes: CustomAttribute[],
) => {
  return z.object({
    name: z
      .string()
      .trim()
      .min(
        1,
        t(
          "apps.admin.pages.companies.new.page.company.form.info.name.required",
        ),
      ),
    address: z.string().min(1, {
      message: t(
        "apps.admin.pages.companies.new.page.company.form.info.address.required",
      ),
    }),
    registeredNumber: z.string().min(1, {
      message: t(
        "apps.admin.pages.companies.new.page.company.form.info.registeredNumber.required",
      ),
    }),
    acknowledgment: z.boolean().optional(),
    customAttributes: createCustomAttributesSchema(t, customAttributes),
  });
};

export type ManualCompanyFormSchema = z.infer<
  ReturnType<typeof manualCompanyFormSchema>
>;

export const mohreCompanyFormSchema = (t: (s: string) => string) => {
  return z.object({
    name: z
      .string()
      .trim()
      .min(
        1,
        t(
          "apps.admin.pages.companies.new.page.company.form.info.name.required",
        ),
      ),
    address: z.string().min(1, {
      message: t(
        "apps.admin.pages.companies.new.page.company.form.info.address.required",
      ),
    }),
    acknowledgment: z.boolean().optional(),
    customAttributes: z
      .record(z.string(), z.custom<string>().optional())
      .optional(),
  });
};

export type MohreCompanyFormSchema = z.infer<
  ReturnType<typeof mohreCompanyFormSchema>
>;
